import type { KitchenModifier, KitchenOrder, KitchenProduct } from '@/types'

import ProductsGrouper from './productsGrouper'

export type MappedModifier = {
  name: string
  quantity: number
  deleted: boolean
  added: boolean
}

export type KitchenProductFormatted = {
  quantity: {
    old: number
    new: number
    current: number | undefined
  }
  separator?: boolean
  id: string
  deleted: boolean
  added: boolean
  name: string
  modified: boolean
  modifiers: MappedModifier[]
  fromCombo?: boolean
  comboId?: string
  comboName?: string
  comments: {
    text?: string
    changed?: boolean
  }
  combine: boolean
  categoryPosition?: number
}

export type KitchenComboFormatted = KitchenProductFormatted & {
  name: string
  comboId?: string
  comboName?: string
  position: number
  products: KitchenProductFormatted[]
}

export type Course = {
  course: string
  products: (KitchenProductFormatted | KitchenComboFormatted)[]
}

export type KitchenOrderFormatted = KitchenOrder & {
  courses: Course[]
  modifiedOrder: boolean
  tableName?: string
}

type TabProductFormatted = Omit<KitchenProduct, 'tabProductId' | 'catalogProductId'> & {
  quantity: number
  groupedId: string
  comboId?: string
  comboName?: string
  categoryPosition?: number
}

function groupByCourse(products: KitchenProduct[]) {
  return products.reduce(
    (groupedProducts, product) => {
      groupedProducts[product.course] = [...(groupedProducts[product.course] || []), product]
      return groupedProducts
    },
    {} as Record<string, KitchenProduct[]>
  )
}

function getModifierId(modifier: KitchenModifier) {
  return modifier.tabModifierId || modifier.catalogModifierId!
}

function mapModifiers(
  oldModifiers: KitchenModifier[],
  newModifiers: KitchenModifier[]
): MappedModifier[] {
  const newModifierIds = newModifiers.map(getModifierId)
  const oldModifierIds = oldModifiers.map(getModifierId)
  const allModifiers = [...oldModifiers, ...newModifiers].reduce(
    (modifiers, modifier) => {
      modifiers[getModifierId(modifier)] = modifier
      return modifiers
    },
    {} as Record<string, KitchenModifier>
  )
  const orderedIds = [...newModifierIds, ...oldModifierIds].filter(
    (el, i, a) => i === a.indexOf(el)
  )
  return orderedIds
    .map(id => allModifiers[id])
    .map(modifier => ({
      name: modifier.name,
      quantity: modifier.quantity,
      deleted: !newModifierIds.includes(getModifierId(modifier)),
      added: !oldModifierIds.includes(getModifierId(modifier))
    }))
}

function mapProduct(
  oldProduct: TabProductFormatted,
  newProduct: TabProductFormatted
): KitchenProductFormatted {
  const product = { ...oldProduct, ...newProduct }
  const modifiers = mapModifiers(
    (oldProduct && oldProduct.modifiers) || [],
    (newProduct && newProduct.modifiers) || []
  )
  const modified = modifiers.some(modifier => modifier.deleted || modifier.added)
  const quantity = {
    old: (oldProduct && oldProduct.quantity) || product.quantity,
    new: (newProduct && newProduct.quantity) || product.quantity
  } as { old: number; new: number; current: number | undefined }

  if (quantity.new === quantity.old) {
    quantity.current = quantity.new
  } else if (quantity.new > quantity.old && !modified) {
    quantity.current = quantity.new - quantity.old
  }

  const oldComments = oldProduct && oldProduct.comments
  const newComments = newProduct && newProduct.comments

  return {
    quantity,
    id: product.groupedId,
    deleted: !newProduct,
    added: !oldProduct,
    name: product.name,
    modified,
    modifiers,
    fromCombo: product.fromCombo,
    comboId: product.comboId,
    comboName: product.comboName,
    comments: {
      text: product.comments,
      changed: newComments !== oldComments
    },
    combine: product.combine,
    categoryPosition: product.categoryPosition
  }
}

function mapProducts(
  oldProducts: TabProductFormatted[],
  newProducts: TabProductFormatted[]
): KitchenProductFormatted[] {
  const oldProductIds = oldProducts.map(product => product.groupedId)
  const newProductIds = newProducts.map(product => product.groupedId)
  const allProductIds = [...new Set([...oldProductIds, ...newProductIds])]
  const oldProductsDict = oldProducts.reduce(
    (products, product) => {
      products[product.groupedId] = product
      return products
    },
    {} as Record<string, TabProductFormatted>
  )
  const newProductsDict = newProducts.reduce(
    (products, product) => {
      products[product.groupedId] = product
      return products
    },
    {} as Record<string, TabProductFormatted>
  )

  return allProductIds.map(id => mapProduct(oldProductsDict[id], newProductsDict[id]))
}

function mapCourses(
  oldProducts: Record<string, KitchenProduct[]>,
  newProducts: Record<string, KitchenProduct[]>,
  onlyModifications: boolean,
  sortedCourses: string[]
): Course[] {
  const oldCourses = Object.keys(oldProducts)
  const newCourses = Object.keys(newProducts)
  const allCourses = [...new Set([...oldCourses, ...newCourses, 'Main'])]
  const missingCourses = allCourses.filter(course => !sortedCourses.includes(course))
  sortedCourses = [...sortedCourses, ...missingCourses]
  return sortedCourses
    .map(course => {
      const products = mapProducts(
        ProductsGrouper.group(oldProducts[course] || []),
        ProductsGrouper.group(newProducts[course] || [])
      ).filter(
        product =>
          !onlyModifications ||
          product.modified ||
          product.quantity.old != product.quantity.new ||
          product.deleted ||
          product.comments.changed ||
          product.added
      )
      const combinedComboId = 1
      const combos = products.reduce(
        (res, product, position) => {
          const combo = res[product.combine ? combinedComboId : product.comboId || product.id] || {
            comboId: product.comboId,
            comboName: product.comboName,
            position,
            products: []
          }
          combo.products = [...combo.products, product]
          res[product.combine ? combinedComboId : product.comboId || product.id] = combo
          return res
        },
        {} as Record<string, KitchenComboFormatted>
      )
      const combosAndProducts: (KitchenProductFormatted | KitchenComboFormatted)[] = Object.values(
        combos
      )
        .sort((c1, c2) => {
          if (!!c1.comboId != !!c2.comboId) {
            if (c1.comboId) return 1
            else return -1
          }
          return c1.position - c2.position
        })
        .flatMap((combo): (KitchenProductFormatted | KitchenComboFormatted)[] => {
          combo.products = combo.products.sort(
            (a, b) => (a.categoryPosition || 0) - (b.categoryPosition || 0)
          )
          if (combo.comboId) {
            return [combo]
          } else {
            return combo.products
          }
        })
      return {
        course,
        products: combosAndProducts
      }
    })
    .filter(course => course.products.length > 0)
}

function format(
  order: KitchenOrder & { tableName?: string },
  sortedCourses: string[]
): KitchenOrderFormatted {
  const newVersion = order.versions.slice(-1)[0]
  let oldVersion = newVersion
  let onlyModifications = false
  if (order.versions.length > 1) {
    oldVersion = order.versions.slice(-2)[0]
    onlyModifications = true
  }
  const newProducts = groupByCourse(newVersion.products)
  const oldProducts = groupByCourse(oldVersion.products)
  const courses = mapCourses(oldProducts, newProducts, onlyModifications, sortedCourses)
  const modifiedOrder = courses
    .flatMap(course => course.products)
    .flatMap(product => {
      if ('comboId' in product && product.comboId && !('combine' in product && product.combine)) {
        return (product as KitchenComboFormatted).products
      } else {
        return [product as KitchenProductFormatted]
      }
    })
    .some(product => product.modified || product.deleted)

  return {
    ...order,
    courses,
    modifiedOrder
  }
}

export default {
  format
}
